import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useMutation } from '@tanstack/react-query';

import { useCan } from '@/app/casl/AbilityContext';
import { RESOURCES } from '@/app/casl/resources';
import { AdminAction } from '@/app/models/AdminAction';

import { SystemNotification } from '../../../components/feedback/SystemNotification';
import DragAndDropFileUploader from '../../../modules/table/components/DragAndDropFileUploader';
import { uploadPaymentDocument } from '../core/_requests';

type Props = {
  show: boolean;
  installmentId: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];

const UploadPaymentDocModal = ({ show, installmentId, onClose, onSuccess }: Props) => {
  const canChangeDashboard = useCan(RESOURCES.ESPACO_GESTOR_DASHBOARD, AdminAction.ALTERAR);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const { mutate: upload, isPending } = useMutation({
    mutationFn: (formData: FormData) => uploadPaymentDocument(installmentId, formData),

    onSuccess: () => {
      SystemNotification.success('Documento(s) enviado(s) com sucesso');
      setSelectedFiles([]);
      onSuccess?.();
      onClose();
    },

    onError: () => {
      SystemNotification.error('Falha ao enviar documento(s)');
    },
  });

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (!canChangeDashboard) return;
    if (selectedFiles.length === 0) return;
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });
    upload(formData);
  };

  const handleClose = () => {
    if (!isPending) {
      setSelectedFiles([]);
      onClose();
    }
  };

  const showUpload = show && canChangeDashboard;

  return (
    <Modal show={showUpload} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Enviar Documento</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <DragAndDropFileUploader multiple accept={ALLOWED_EXTENSIONS} maxSize={10} onFileUpload={handleFilesSelected} />
        {selectedFiles.length > 0 && (
          <div className="d-flex flex-column gap-3 mt-4">
            {selectedFiles.map((file, index) => (
              <div
                key={`${file.name}-${file.size}-${file.lastModified}`}
                className="d-flex align-items-center justify-content-between border border-dashed border-gray-300 rounded p-4"
              >
                <div className="d-flex align-items-center gap-3">
                  <i className="bi bi-file-earmark fs-2 text-primary" />
                  <div>
                    <div className="fw-bold text-gray-800">{file.name}</div>
                    <div className="text-gray-500 fs-7">{(file.size / 1024).toFixed(0)} KB</div>
                  </div>
                </div>
                <button
                  className="btn btn-icon btn-light-danger btn-sm"
                  disabled={isPending}
                  title="Remover"
                  type="button"
                  onClick={() => handleRemoveFile(index)}
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" disabled={isPending} type="button" onClick={handleClose}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          disabled={selectedFiles.length === 0 || isPending}
          type="button"
          onClick={handleUpload}
        >
          {isPending ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Enviando...
            </>
          ) : (
            'Enviar'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploadPaymentDocModal;
